import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { sortNodesBySortOrderFirst } from '../../../../utils/applyMenuConfigChanges/addSortOrderProperty/sortBySortOrder';
import { isSubTreeKey, sectionPrefix } from '../../NavigationMenu/utils';
import { useSelector } from 'react-redux';
import { selectOpenSections } from '../../../../common/redux/features/docsMenu/selectors';
import { IndexPage } from '../IndexPage';
import { ApiSectionMembers } from './ApiSectionMembers';

interface ApiSectionViewProps {
  node: any;
  prefix: string;
  shouldOpenSubCategory: boolean;
  depth: number;
}

export function ApiSectionView({
  node,
  prefix,
  shouldOpenSubCategory,
  depth,
}: ApiSectionViewProps) {
  const history = useHistory();
  const location = useLocation();
  const openedSections = useSelector(selectOpenSections);
  const [shouldShowMore, setShouldShowMore] = useState(false);

  const nodePrefix = useMemo(() => {
    return prefix
      ? sectionPrefix(prefix, node.data.urlFormat)
      : node.data.urlFormat;
  }, [prefix, node]);

  const isCurrentNodeIdIncludedInInitialUrl = useMemo(() => {
    return node?.data?.urlFormat && location.pathname.includes(nodePrefix);
  }, [nodePrefix, node, location]);

  const shouldShowMembers = useMemo(() => {
    return (
      shouldOpenSubCategory ||
      depth === 0 ||
      shouldShowMore ||
      isCurrentNodeIdIncludedInInitialUrl ||
      !!openedSections[nodePrefix] ||
      node.type === 'TAG'
    );
  }, [
    shouldShowMore,
    shouldOpenSubCategory,
    nodePrefix,
    depth,
    node,
    isCurrentNodeIdIncludedInInitialUrl,
    openedSections,
  ]);

  const sortedNodeChildrenKeys = useMemo(
    () => sortNodesBySortOrderFirst(node).filter(isSubTreeKey),
    [node],
  );

  useEffect(() => {
    if (shouldShowMore) {
      const indexPageElement = document.getElementById(nodePrefix);
      indexPageElement &&
        indexPageElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      history.push(`/${nodePrefix}${location.search || ''}`);
    }
  }, [shouldShowMore]);

  return (
    <div key={`${nodePrefix}__frag`}>
      {node.type === 'TAG' ? (
        <span id={nodePrefix} />
      ) : (
        <IndexPage
          depth={depth}
          node={node}
          onShowMoreClick={onShowMoreClick}
          isOpened={shouldShowMembers}
          prefix={nodePrefix}
        />
      )}
      {shouldShowMembers && (
        <ApiSectionMembers
          childrenKeys={sortedNodeChildrenKeys}
          prefix={nodePrefix}
          node={node}
          depth={depth}
        />
      )}
    </div>
  );

  function onShowMoreClick() {
    setShouldShowMore(true);
  }
}
